import { useEffect, useRef } from 'react';
import {
  queryOptions,
  type QueryClient,
  skipToken,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  DiscoveryEstimateBody,
  DiscoveryJobView,
  DiscoveryLiveCreator,
  DiscoveryLiveFeed,
  DiscoveryMediaItem,
  DiscoveryResults,
} from '@discovr/types';
import { isDiscoveryJobStatus } from '@discovr/types';
import { ApiError } from '@discovr/common';
import { discoveryApi, mediaUrl } from '../api/discoveryApi';
import { createMediaSigner, type SignMedia } from '../api/mediaSigner';
import { creatorKey, handleKey } from '../creators';
import {
  LIVE_FEED_POLL_MS,
  LIVE_JOB_STATUSES,
  MEDIA_LINKS_STALE_MS,
  PICKS_POLL_MS,
  POLL_BACKOFF_MAX_FACTOR,
  RESULTS_POLL_MS,
  SEARCH_POLL_MS,
} from '../constants';
import { discoveryKeys, invalidateSettledSearch } from '../queryKeys';

export const isLiveRun = (run: { status: string }) =>
  isDiscoveryJobStatus(run.status) && LIVE_JOB_STATUSES.has(run.status);

export const isLiveJob = (job: DiscoveryJobView | undefined) => job !== undefined && isLiveRun(job);

/** A 4xx other than 429 (job gone, grant revoked) answers the same on every retry, so a poll stops on it. */
export const stopsPolling = (error: unknown) =>
  error instanceof ApiError &&
  error.httpStatus >= 400 &&
  error.httpStatus < 500 &&
  error.httpStatus !== 429;

const isRateLimited = (error: unknown) => error instanceof ApiError && error.httpStatus === 429;

/** Polls every `ms`; each failed fetch in a row after a 429 doubles that, up to `POLL_BACKOFF_MAX_FACTOR` times, and a success resets it. */
export const pollEvery =
  (ms: number, live = true) =>
  (q: { state: { error: unknown; fetchFailureCount: number } }) => {
    if (!live || stopsPolling(q.state.error)) return false;
    if (!isRateLimited(q.state.error)) return ms;
    return Math.min(ms * 2 ** q.state.fetchFailureCount, ms * POLL_BACKOFF_MAX_FACTOR);
  };

/** Falls back to the run list so a failed first status fetch still counts as live and keeps polling. */
export const isLiveSearch = (
  job: { status: string } | undefined,
  listed: { status: string } | undefined,
) => (job ? isLiveRun(job) : listed !== undefined && isLiveRun(listed));

/**
 * Polls while the search is queued or running, then stops. When it settles, its results and the
 * campaign's run list are fetched once more: their own polls stop with it and would keep the
 * last in-flight snapshot, missing the final ranking.
 */
export function useSearchJob(
  campaignId: string,
  jobId: string | null,
  listed?: { status: string },
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: discoveryKeys.search(campaignId, jobId),
    queryFn: campaignId && jobId ? () => discoveryApi.search(campaignId, jobId) : skipToken,
    refetchInterval: (q) => pollEvery(SEARCH_POLL_MS, isLiveSearch(q.state.data, listed))(q),
  });
  const live = isLiveSearch(query.data, listed);
  const seen = useRef({ jobId, live });
  useEffect(() => {
    const before = seen.current;
    seen.current = { jobId, live };
    if (jobId === null || before.jobId !== jobId || !before.live || live) return;
    void invalidateSettledSearch(queryClient, campaignId, jobId);
  }, [campaignId, jobId, live, queryClient]);
  return query;
}

function inArrivalOrder(
  before: DiscoveryResults | undefined,
  fresh: DiscoveryResults,
): DiscoveryResults {
  if (!before) return fresh;
  const byCreator = new Map(fresh.results.map((card) => [creatorKey(card), card]));
  const kept = before.results.flatMap((card) => byCreator.get(creatorKey(card)) ?? []);
  const seen = new Set(kept.map(creatorKey));
  return {
    ...fresh,
    results: [...kept, ...fresh.results.filter((card) => !seen.has(creatorKey(card)))],
  };
}

export function useSearchResults(
  campaignId: string,
  jobId: string | null,
  live: boolean,
  foundSoFar?: number,
) {
  const queryClient = useQueryClient();
  const queryKey = discoveryKeys.results(campaignId, jobId);
  const query = useQuery({
    queryKey,
    queryFn:
      campaignId && jobId
        ? async () => {
            const fresh = await discoveryApi.results(campaignId, jobId);
            return live
              ? inArrivalOrder(queryClient.getQueryData<DiscoveryResults>(queryKey), fresh)
              : fresh;
          }
        : skipToken,
    refetchInterval: pollEvery(RESULTS_POLL_MS, live),
  });
  const { refetch } = query;
  const lastFound = useRef(foundSoFar);
  useEffect(() => {
    if (!live || foundSoFar === undefined || foundSoFar === lastFound.current) return;
    lastFound.current = foundSoFar;
    void refetch();
  }, [live, foundSoFar, refetch]);
  return query;
}

export interface LiveRoster {
  next: number;
  creators: DiscoveryLiveCreator[];
}

const EMPTY_ROSTER: LiveRoster = { next: 0, creators: [] };

function mergeRoster(before: LiveRoster, feed: DiscoveryLiveFeed): LiveRoster {
  if (!feed.creators.length) return { ...before, next: feed.next };
  const changed = new Set(feed.creators.map((creator) => handleKey(creator.handle)));
  return {
    next: feed.next,
    creators: [
      ...before.creators.filter((c) => !changed.has(handleKey(c.handle))),
      ...feed.creators,
    ],
  };
}

export function useLiveCreators(campaignId: string, jobId: string | null, live: boolean) {
  const queryClient = useQueryClient();
  const queryKey = discoveryKeys.live(campaignId, jobId);
  return useQuery({
    queryKey,
    queryFn:
      campaignId && jobId && live
        ? async () => {
            const before = queryClient.getQueryData<LiveRoster>(queryKey) ?? EMPTY_ROSTER;
            return mergeRoster(before, await discoveryApi.live(campaignId, jobId, before.next));
          }
        : skipToken,
    refetchInterval: pollEvery(LIVE_FEED_POLL_MS, live),
  });
}

export function useRejectedCreators(campaignId: string, jobId: string | null, live: boolean) {
  return useQuery({
    queryKey: discoveryKeys.rejected(campaignId, jobId),
    queryFn: campaignId && jobId ? () => discoveryApi.rejected(campaignId, jobId) : skipToken,
    refetchInterval: pollEvery(RESULTS_POLL_MS, live),
  });
}

export function useSearchEstimate(campaignId: string, body: DiscoveryEstimateBody) {
  return useQuery({
    queryKey: discoveryKeys.estimate(campaignId, body),
    queryFn: () => discoveryApi.estimate(campaignId, body),
    enabled: campaignId.length > 0,
    staleTime: Infinity,
  });
}

export function useCampaignPicks(campaignId: string) {
  return useQuery({
    queryKey: discoveryKeys.picks(campaignId),
    queryFn: () => discoveryApi.picks(campaignId),
    enabled: campaignId.length > 0,
    refetchInterval: pollEvery(PICKS_POLL_MS),
    refetchOnWindowFocus: true,
  });
}

export const mediaKey = (item: DiscoveryMediaItem) =>
  item.kind === 'reel' ? `reel:${item.platformId}:${item.postId}` : `image:${item.url}`;

type SignedLink = readonly [key: string, url: string];

/** Module-level so TanStack Query reuses the combined map while no link changed (an inline function re-runs every render). */
const combineLinks = (results: readonly { data?: SignedLink }[]): ReadonlyMap<string, string> =>
  new Map(results.flatMap((result) => (result.data ? [result.data] : [])));

const signers = new WeakMap<QueryClient, Map<string, SignMedia>>();

/** One signer per client and campaign, so every card and reel asking in the same tick shares a batch. */
export function mediaSignerFor(queryClient: QueryClient, campaignId: string): SignMedia {
  const byCampaign = signers.get(queryClient) ?? new Map<string, SignMedia>();
  signers.set(queryClient, byCampaign);
  const shared =
    byCampaign.get(campaignId) ??
    createMediaSigner((items) => discoveryApi.mediaLinks(campaignId, items));
  byCampaign.set(campaignId, shared);
  return shared;
}

function useMediaSigner(campaignId: string): SignMedia {
  const queryClient = useQueryClient();
  return mediaSignerFor(queryClient, campaignId);
}

const signedLinkQuery = (campaignId: string, sign: SignMedia, item: DiscoveryMediaItem | null) =>
  queryOptions({
    queryKey: discoveryKeys.mediaLink(campaignId, item ? mediaKey(item) : ''),
    queryFn:
      item && campaignId
        ? async (): Promise<SignedLink> => [mediaKey(item), mediaUrl(await sign(item))]
        : skipToken,
    staleTime: MEDIA_LINKS_STALE_MS,
    refetchInterval: MEDIA_LINKS_STALE_MS,
  });

export function useSignedMedia(
  campaignId: string,
  item: DiscoveryMediaItem | null,
): { url: string | undefined; failed: boolean } {
  const sign = useMediaSigner(campaignId);
  const query = useQuery({
    ...signedLinkQuery(campaignId, sign, item),
    select: ([, url]: SignedLink) => url,
  });
  return { url: query.data, failed: query.isError };
}

export function useMediaLinks(
  campaignId: string,
  items: readonly DiscoveryMediaItem[],
): ReadonlyMap<string, string> {
  const sign = useMediaSigner(campaignId);
  const unique = [...new Map(items.map((item) => [mediaKey(item), item])).values()];
  return useQueries({
    queries: unique.map((item) => signedLinkQuery(campaignId, sign, item)),
    combine: combineLinks,
  });
}
